/**
 * Composable: runtime fallback for image alt text.
 *
 * Priority:
 * 1. Page title (CMS)
 * 2. Page slug (CMS)
 * 3. Route param slug
 * 4. Route path segment
 * 5. "Home" (root / index only)
 *
 * Notes:
 * - Designed to be SSR-safe
 * - Defensive against malformed routes
 * - Avoids empty / whitespace-only alt text
 */

import { isAltTextFeatureEnabled } from '~/utils/altTextFeature';

export function usePageAltFallback() {
  const route = useRoute();

  /**
   * Normalizes slug-like strings into readable text
   * Example: "course-detail-page" -> "Course Detail Page"
   */
  const formatSlug = (value) => {
    if (typeof value !== 'string') return '';

    return value
      .trim()
      .split('-')
      .filter(Boolean)
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(' ')
      .replace(/\s+/g, ' ');
  };

  const altFallback = computed(() => {
    if (!isAltTextFeatureEnabled()) {
      return '';
    }

    // NOTE: route.pageData is injected by nuxt/middleware/page.js
    const pageData = route?.pageData?.value ?? route?.pageData;

    // Normalize slug param
    const rawSlug = Array.isArray(route?.params?.slug)
      ? route.params.slug.join('-')
      : typeof route?.params?.slug === 'string'
      ? route.params.slug
      : '';

    // Route path parsing
    const segments = typeof route?.path === 'string'
      ? route.path.split('/').filter(Boolean)
      : [];

    const lastSegment = segments[segments.length - 1] || '';

    return (
      (typeof pageData?.title === 'string' ? pageData.title.trim() : '') ||
      formatSlug(pageData?.slug) ||
      formatSlug(rawSlug) ||
      (lastSegment !== 'index' && formatSlug(lastSegment)) ||
      (segments.length === 0 || lastSegment === 'index' ? 'Home' : '')
    );
  });

  return {
    altFallback
    // formatSlug intentionally kept private
  };
}
